import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useGoogleLogin } from '@react-oauth/google';
import { trackLead } from '../utils/metaPixel';
import { trackSignUp as redditSignUp } from '../utils/redditPixel';
import { clickIdsForRegister } from '../utils/adClickIds';
import '../styles/responsive.css';

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M15.68 8.18c0-.57-.05-1.11-.14-1.64H8v3.1h4.3a3.67 3.67 0 01-1.59 2.4v2h2.57c1.51-1.39 2.4-3.44 2.4-5.86z" fill="#4285F4"/>
    <path d="M8 16c2.16 0 3.97-.72 5.3-1.94l-2.57-2a4.8 4.8 0 01-7.16-2.52H.96v2.06A8 8 0 008 16z" fill="#34A853"/>
    <path d="M3.57 9.54A4.8 4.8 0 013.32 8c0-.53.09-1.05.25-1.54V4.4H.96A8 8 0 000 8c0 1.3.31 2.52.96 3.6l2.61-2.06z" fill="#FBBC05"/>
    <path d="M8 3.2a4.33 4.33 0 013.06 1.2l2.3-2.3A7.7 7.7 0 008 0 8 8 0 00.96 4.4l2.61 2.06A4.77 4.77 0 018 3.2z" fill="#EA4335"/>
  </svg>
);

const GoogleLoginButton: React.FC<{
  onSuccess: (accessToken: string) => void;
  onError: () => void;
  disabled?: boolean;
}> = ({ onSuccess, onError, disabled }) => {
  const login = useGoogleLogin({
    onSuccess: (r) => onSuccess(r.access_token),
    onError,
  });
  return (
    <button onClick={() => login()} disabled={disabled}
      style={{
        width: '100%', padding: '11px 0', background: 'transparent',
        border: `1px solid ${BR}`, borderRadius: 8, color: T1,
        fontSize: 13, fontWeight: 600, cursor: 'pointer',
        fontFamily: 'inherit', display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: 10, marginBottom: 0,
        transition: 'border-color .18s, background .18s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.background = C2; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = BR; e.currentTarget.style.background = 'transparent'; }}>
      <GoogleIcon /> Continue with Google
    </button>
  );
};

interface RegisterPageProps {
  onRegisterSuccess: (user: any) => void;
}

/* ── Design tokens */
const BG = '#0a0a0a';
const C1 = '#111111';
const C2 = '#161616';
const BR = '#1e1e1e';
const A  = '#4EDEA3';
const T1 = '#f0f0f0';
const T2 = '#b0b0b0';
const T3 = '#909090';

const RegisterPage: React.FC<RegisterPageProps> = ({ onRegisterSuccess }) => {
  const navigate = useNavigate();
  const [username,        setUsername       ] = useState('');
  const [email,           setEmail          ] = useState('');
  const [password,        setPassword       ] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error,           setError          ] = useState('');
  const [loading,         setLoading        ] = useState(false);
  const [focused,         setFocused        ] = useState<string | null>(null);

  const inputStyle = (field: string): React.CSSProperties => ({
    width: '100%', padding: '11px 14px',
    background: C2, border: `1px solid ${focused === field ? A : BR}`,
    borderRadius: 8, color: T1, fontSize: 14, fontFamily: 'inherit',
    outline: 'none', transition: 'border-color .18s', boxSizing: 'border-box',
  });

  /* ── Email/password register */
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await axios.post('/api/users/register', {
        username, email, password,
        adClickIds: clickIdsForRegister(), // null if no ad-click attribution data stored
      });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.removeItem('elabs_active_tab'); // always land on overview after fresh registration
      // Ad-attribution pixels — fire on successful account creation
      trackLead({ method: 'email' });        // Meta
      redditSignUp({ method: 'email' });     // Reddit
      onRegisterSuccess(res.data.user);
      navigate('/intelligence');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  /* ── Google OAuth */
  const handleGoogleSuccess = async (accessToken: string) => {
    setLoading(true); setError('');
    try {
      const res = await axios.post('/api/users/auth/google', {
        access_token: accessToken,
        adClickIds: clickIdsForRegister(), // server stores on User.adClickIds if user is new
      });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.removeItem('elabs_active_tab'); // always land on overview after fresh registration
      // Only fire SignUp/Lead for NEW Google signups, not returning logins
      if (res.data.isNew) {
        trackLead({ method: 'google' });         // Meta
        redditSignUp({ method: 'google' });      // Reddit
      }
      onRegisterSuccess(res.data.user);
      navigate('/intelligence');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Google sign-in failed.');
    } finally { setLoading(false); }
  };

  const hasGoogle = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

  return (
    <div style={{
      background: BG, minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: "'DM Sans',-apple-system,sans-serif",
    }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ width: '100%', maxWidth: 400 }}
      >
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <p style={{
            fontFamily: "'Space Grotesk',sans-serif", fontSize: 22,
            fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', margin: '0 0 4px',
          }}>Electa Labs</p>
          <p style={{ fontSize: 11, color: A, fontWeight: 700, letterSpacing: '0.12em', margin: 0 }}>
            SIGNAL · NOT NOISE
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: C1, border: `1px solid ${BR}`,
          borderRadius: 16, padding: '32px 28px',
        }}>
          <h1 style={{
            fontFamily: "'Space Grotesk',sans-serif", fontSize: 22,
            fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', margin: '0 0 6px',
          }}>Start your free trial</h1>
          <p style={{ fontSize: 13, color: T3, margin: '0 0 16px' }}>
            Full Pro access for 7 days. No credit card required.
          </p>

          {/* Trust badges — single row on most viewports, wraps on tiny phones */}
          <div className="auth-trust-badges" style={{
            display: 'flex', gap: 5, flexWrap: 'nowrap', marginBottom: 24,
            justifyContent: 'flex-start',
          }}>
            {['7-day Pro trial', 'No credit card', 'Cancel any time'].map(label => (
              <span key={label} style={{
                fontSize: 10, fontWeight: 600, color: A,
                background: `${A}10`, border: `1px solid ${A}25`,
                borderRadius: 100, padding: '3px 9px',
                whiteSpace: 'nowrap', flexShrink: 1,
              }}>✓ {label}</span>
            ))}
          </div>


          {/* Error */}
          {error && (
            <div style={{
              marginBottom: 16, padding: '10px 14px', borderRadius: 8,
              background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.2)',
              color: '#f87171', fontSize: 13,
            }}>{error}</div>
          )}

          <form onSubmit={handleRegister}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T3, letterSpacing: '0.08em', marginBottom: 6 }}>USERNAME</label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)}
                required placeholder="Choose a username"
                onFocus={() => setFocused('username')} onBlur={() => setFocused(null)}
                style={inputStyle('username')} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T3, letterSpacing: '0.08em', marginBottom: 6 }}>EMAIL</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                required placeholder="you@example.com"
                onFocus={() => setFocused('email')} onBlur={() => setFocused(null)}
                style={inputStyle('email')} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T3, letterSpacing: '0.08em', marginBottom: 6 }}>PASSWORD</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                required placeholder="Min. 8 characters"
                onFocus={() => setFocused('password')} onBlur={() => setFocused(null)}
                style={inputStyle('password')} />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: T3, letterSpacing: '0.08em', marginBottom: 6 }}>CONFIRM PASSWORD</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                required placeholder="••••••••"
                onFocus={() => setFocused('confirm')} onBlur={() => setFocused(null)}
                style={inputStyle('confirm')} />
            </div>

            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '12px 0', background: A, color: '#000',
              border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 800,
              cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              opacity: loading ? 0.7 : 1, transition: 'opacity .18s',
            }}>
              {loading ? 'Creating account…' : 'Start free trial'}
            </button>
          </form>

          {/* Google button — below the main form */}
          {hasGoogle && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0 16px' }}>
                <div style={{ flex: 1, height: 1, background: BR }} />
                <span style={{ fontSize: 11, color: T3, fontWeight: 600, letterSpacing: '0.06em' }}>OR</span>
                <div style={{ flex: 1, height: 1, background: BR }} />
              </div>
              <GoogleLoginButton
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Google sign-in failed. Please try again.')}
                disabled={loading}
              />
            </>
          )}

          <p style={{ fontSize: 13, color: T3, textAlign: 'center', margin: '20px 0 0' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: A, textDecoration: 'none', fontWeight: 600 }}>Sign in</Link>
          </p>
        </div>

        <p style={{ fontSize: 12, color: T3, textAlign: 'center', marginTop: 20, lineHeight: 1.6 }}>
          By creating an account you agree to our{' '}
          <Link to="/terms" style={{ color: T2, textDecoration: 'none' }}>Terms of Service</Link> and{' '}
          <Link to="/privacy" style={{ color: T2, textDecoration: 'none' }}>Privacy Policy</Link>.
        </p>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
